import { useCallback, useEffect, useState } from 'react';

/**
 * Três estados, não dois.
 *
 * O toggle de dois estados tem um defeito concreto: quem escolhe claro durante
 * o dia e tem o sistema programado para virar escuro à noite volta a ver a tela
 * escura contra a própria escolha — e não consegue pedir "siga o sistema" de
 * volta, porque o botão já está mostrando o estado atual. Guardar a INTENÇÃO
 * ("sistema") separada do resultado ("claro"/"escuro") resolve isso.
 */
export type Tema = 'sistema' | 'claro' | 'escuro';

const CHAVE = 'tema';

function ler(): Tema {
  try {
    const v = localStorage.getItem(CHAVE);
    return v === 'claro' || v === 'escuro' ? v : 'sistema';
  } catch {
    // Navegador com armazenamento bloqueado (janela anônima, política de
    // privacidade): a tela ainda funciona, só não lembra a escolha.
    return 'sistema';
  }
}

/** Precisa casar com --canvas de cada tema em theme-base.css. */
const FUNDO = { light: '#faf9f7', dark: '#0d0d0f' } as const;

/**
 * Aplica no <html>. Ausência de `data-theme` significa "siga o sistema" — é o
 * que o CSS espera, e é o mesmo contrato do script inline do index.html que
 * pinta o tema antes do primeiro quadro.
 */
function aplicar(tema: Tema): void {
  const raiz = document.documentElement;
  if (tema === 'sistema') delete raiz.dataset.theme;
  else raiz.dataset.theme = tema === 'claro' ? 'light' : 'dark';

  // A barra do navegador tem que acompanhar a ESCOLHA, não só o sistema: o
  // media query das tags <meta> não enxerga o data-theme, então quem escolhe
  // escuro num sistema claro ficaria com a barra branca sobre a página escura.
  const escuro =
    raiz.dataset.theme === 'dark' ||
    (!raiz.dataset.theme && window.matchMedia('(prefers-color-scheme: dark)').matches);
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    meta.removeAttribute('media');
    meta.content = escuro ? FUNDO.dark : FUNDO.light;
  }
}

export function useTema(): { tema: Tema; definir: (t: Tema) => void } {
  const [tema, setTema] = useState<Tema>(ler);

  useEffect(() => {
    aplicar(tema);
    if (tema !== 'sistema') return;
    // No modo "sistema", o aparelho pode virar claro/escuro com a página
    // aberta (agendamento por horário). Sem isto a barra do navegador ficaria
    // na cor antiga até a próxima recarga.
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const ouvir = () => aplicar('sistema');
    mq.addEventListener('change', ouvir);
    return () => mq.removeEventListener('change', ouvir);
  }, [tema]);

  const definir = useCallback((t: Tema) => {
    setTema(t);
    try {
      if (t === 'sistema') localStorage.removeItem(CHAVE);
      else localStorage.setItem(CHAVE, t);
    } catch {
      /* sem armazenamento: vale para esta sessão */
    }
  }, []);

  return { tema, definir };
}
